import React, { useEffect, useState } from 'react'
import type { ZoomDiagnostics } from './VideoPlayer360Controller'

// ?zoominfo=1 — a measuring panel for one question (Gavin, 28 Sep): zoomed
// all the way out, Chrome and Brave on a Mac show far more of the room than
// Safari and Firefox. He opens the same link in two browsers, zooms fully out
// in each, and sends a screenshot of each; the numbers that differ are the
// cause. Nothing renders, and nothing polls, without the parameter.
//
// Captured when the module first loads, for the reason designPreview.ts gives:
// the public page's redirect to a video's canonical slug drops the query
// string before the player mounts.
const initialSearch = typeof window === 'undefined' ? '' : window.location.search
const flag = new URLSearchParams(initialSearch).get('zoominfo')
export const zoomReadoutEnabled = flag === '1' || flag === 'true'

// Enough to tell the four browsers in question apart; the full user agent is
// shown too, in case this guesses wrong.
const browserName = (ua: string): string => {
  if (/Firefox\//.test(ua)) return 'Firefox'
  if (/Edg\//.test(ua)) return 'Edge'
  if (/Chrome\//.test(ua)) return (navigator as Navigator & { brave?: unknown }).brave ? 'Brave' : 'Chrome'
  if (/Safari\//.test(ua)) return 'Safari'
  return 'unknown'
}

const n = (x: number, digits = 2) => (Number.isFinite(x) ? x.toFixed(digits) : String(x))

const PRESET_NAMES: Record<ZoomDiagnostics['preset'], string> = {
  default: 'normal',
  topDown: 'above',
  bottomUp: 'below',
}

interface ZoomReadoutProps {
  read: () => ZoomDiagnostics | null
}

const ZoomReadout: React.FC<ZoomReadoutProps> = ({ read }) => {
  const [d, setD] = useState<ZoomDiagnostics | null>(null)
  useEffect(() => {
    const id = window.setInterval(() => setD(read()), 250)
    return () => window.clearInterval(id)
  }, [read])

  const ua = navigator.userAgent
  const rows: [string, string][] = d ? [
    ['browser', browserName(ua)],
    ['view', PRESET_NAMES[d.preset]],
    ['zoom', `${n(d.zoom, 3)}  (limits ${n(d.minZoom)}–${n(d.maxZoom)})`],
    ['widest', d.widest ? `${n(d.widest.zoom, 3)} in ${PRESET_NAMES[d.widest.preset]}, ${n(d.widest.fovAcross, 0)}° across` : '—'],
    ['angle', `${n(d.fovAcross, 1)}° across × ${n(d.fovUpDown, 1)}° up-down  (lens ${n(d.lensFov, 0)}°)`],
    ['player', `${d.containerW}×${d.containerH} css px, aspect ${n(d.aspect, 3)}`],
    ['canvas', `${d.canvasW}×${d.canvasH} px, ratio ${n(d.pixelRatio)} (screen ${n(d.devicePixelRatio)})`],
    ['tuning', `${d.phoneTuning ? 'phone' : 'desktop'}  (orientation permission API ${d.requestPermissionApi ? 'present' : 'absent'}, touch points ${d.maxTouchPoints})`],
    ['wheel', d.lastWheel
      ? `${d.wheelEvents} events, last deltaY ${n(d.lastWheel.deltaY, 1)} mode ${d.lastWheel.deltaMode}${d.lastWheel.ctrlKey ? ' ctrl' : ''}`
      : `${d.wheelEvents} events`],
    ['pinch', `${d.gestureEvents} Safari gesture events`],
  ] : [['360', 'no sphere on screen (grid view, or still loading)']]

  return (
    <div
      data-player-controls
      style={{
        position: 'absolute',
        top: 44,
        left: 12,
        zIndex: 11,
        maxWidth: 'calc(100% - 24px)',
        padding: '8px 10px',
        background: 'rgba(0,0,0,0.78)',
        color: '#fff',
        font: '12px/1.45 ui-monospace, Menlo, Consolas, monospace',
        borderRadius: 4,
        pointerEvents: 'auto',
        userSelect: 'text',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4 }}>zoom readout — zoom all the way out, then screenshot</div>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', gap: 10 }}>
          <span style={{ opacity: 0.6, minWidth: 52 }}>{k}</span>
          <span>{v}</span>
        </div>
      ))}
      <div style={{ opacity: 0.5, marginTop: 4, fontSize: 10, wordBreak: 'break-all' }}>{ua}</div>
    </div>
  )
}

export default ZoomReadout
